import { useEffect, useState } from "react";
import Lenis from "lenis";
import { motion, useScroll, useSpring } from "framer-motion";

import SpaceScene from "../scene/SpaceScene";
import Navbar from "../components/Navbar";
import Hero from "../sections/Hero";
import Manifesto from "../sections/Manifesto";
import Marquee from "../sections/Marquee";
import Services from "../sections/Services";
import Nonprofit from "../sections/Nonprofit";
import SchedulingTeaser from "../sections/SchedulingTeaser";
import DataCenter from "../sections/DataCenter";
import AICore from "../sections/AICore";
import GovernedState from "../sections/GovernedState";
import Showreel from "../sections/Showreel";
import Research from "../sections/Research";
import Industries from "../sections/Industries";
import Results from "../sections/Results";
import Contact from "../sections/Contact";
import Footer from "../sections/Footer";

export default function LandingPage() {
  const { scrollYProgress } = useScroll();
  const bar = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Lenis hijacks touch scrolling and causes stutter/freezing on phones —
    // use native scroll on touch / small / reduced-motion devices.
    const touch = window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 820;
    if (reduce || touch) return;
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) {
        if (window.__lenis) window.__lenis.scrollTo(el, { immediate: true });
        else el.scrollIntoView();
      }
    }, 900);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="App noise relative bg-nexis-bg text-white" data-testid="app-root">
      <div className="fixed inset-0 z-0">
        <SpaceScene />
        <div className="pointer-events-none absolute inset-0 grid-lines opacity-60" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-nexis-bg/10 to-nexis-bg" />
      </div>

      <motion.div
        className="fixed left-0 top-0 z-[80] h-[2px] w-full origin-left bg-gradient-to-r from-nexis-blue to-nexis-purple"
        style={{ scaleX: bar }}
        data-testid="scroll-progress"
      />

      <Navbar />

      <main className="relative z-10">
        <Hero ready={ready} />
        <Manifesto />
        <Marquee />
        <Services />
        <Nonprofit />
        <SchedulingTeaser />
        <DataCenter />
        <AICore />
        <GovernedState />
        <Showreel />
        <Research />
        <Industries />
        <Results />
        <Contact />
        <Footer />
      </main>
    </div>
  );
}
